import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, Loader2, Mail, ShieldCheck, AlertTriangle, RefreshCw, PencilLine, Clock, CheckCircle2, Copy } from "lucide-react";
import { fieldLabels } from "@/lib/intakeFixFields";
import { toast } from "sonner";
import { hasDeclaredValue } from "@/lib/allergyUtils";
import { confidencePct, needsPassportAttention, verdictLabel, needsIdPhotoAttention, idPhotoVerdictLabel } from "@/lib/passportDocCheck";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  submission: any | null;
  onEmail?: (submission: any) => void;
  onVerify?: (id: string, docKind?: "passport" | "id_photo") => void;
  verifying?: boolean;
  onRequestFix?: (submission: any, presetKeys?: string[]) => void;
  fixRequest?: any | null;
}


function Field({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm">{Array.isArray(value) ? value.join(", ") : String(value)}</p>
    </div>
  );
}

export function SubmissionDetailDialog({ open, onOpenChange, submission, onEmail, onVerify, verifying, onRequestFix, fixRequest }: Props) {
  const qc = useQueryClient();
  const [idUrl, setIdUrl] = useState<string | null>(null);
  const [ppUrl, setPpUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [overriding, setOverriding] = useState(false);

  useEffect(() => {
    if (!open || !submission) { setIdUrl(null); setPpUrl(null); return; }
    setLoading(true);
    (async () => {
      const tasks: Promise<any>[] = [];
      if (submission.id_photo_path) tasks.push(supabase.storage.from("documents").createSignedUrl(submission.id_photo_path, 3600));
      else tasks.push(Promise.resolve({ data: null }));
      if (submission.passport_photo_path) tasks.push(supabase.storage.from("documents").createSignedUrl(submission.passport_photo_path, 3600));
      else tasks.push(Promise.resolve({ data: null }));
      const [id, pp] = await Promise.all(tasks);
      setIdUrl(id?.data?.signedUrl || null);
      setPpUrl(pp?.data?.signedUrl || null);
      setLoading(false);
    })();
  }, [open, submission]);

  async function markVerified(field: "passport_doc_verified_by" | "id_doc_verified_by" = "passport_doc_verified_by") {
    if (!submission) return;
    setOverriding(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await (supabase as any)
        .from("student_intake_submissions")
        .update({ [field]: auth?.user?.id ?? null })
        .eq("id", submission.id);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["student-intake-subs", submission.lead_id] });
      toast.success("Marked as manually confirmed");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Could not update");
    } finally {
      setOverriding(false);
    }
  }


  if (!submission) return null;

  const flagged = needsPassportAttention(submission);
  const idFlagged = needsIdPhotoAttention(submission);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{submission.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {onEmail && (
              <Button variant="outline" size="sm" onClick={() => onEmail(submission)} disabled={!submission.email}>
                <Mail className="h-4 w-4 mr-1" /> Email student
              </Button>
            )}
            {onRequestFix && (
              <Button variant="outline" size="sm" onClick={() => onRequestFix(submission)}>
                <PencilLine className="h-4 w-4 mr-1" /> Request correction
              </Button>
            )}
            {onVerify && submission.passport_photo_path && (
              <Button variant="outline" size="sm" onClick={() => onVerify(submission.id, "passport")} disabled={verifying}>
                {verifying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                {submission.passport_doc_verdict ? "Re-check passport" : "Check passport"}
              </Button>
            )}
            {onVerify && submission.id_photo_path && (
              <Button variant="outline" size="sm" onClick={() => onVerify(submission.id, "id_photo")} disabled={verifying}>
                {verifying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                {submission.id_doc_verdict ? "Re-check ID photo" : "Check ID photo"}
              </Button>
            )}
          </div>

          {fixRequest && fixRequest.status !== "cancelled" && (
            <div className="rounded-md border p-3 bg-muted/40 space-y-1">
              <div className="flex items-center gap-2">
                {fixRequest.status === "completed"
                  ? <CheckCircle2 className="h-4 w-4 text-primary" />
                  : <Clock className="h-4 w-4 text-amber-600" />}
                <p className="text-sm font-medium">
                  {fixRequest.status === "completed" ? "Correction received" : "Correction requested"}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Items: {fieldLabels(fixRequest.completed_fields || fixRequest.requested_fields).join(", ") || "—"}
              </p>
              {fixRequest.note && <p className="text-xs italic text-muted-foreground">"{fixRequest.note}"</p>}
              <p className="text-xs text-muted-foreground">
                {fixRequest.sent_at
                  ? `Emailed ${new Date(fixRequest.sent_at).toLocaleString()}${fixRequest.send_count > 1 ? ` (${fixRequest.send_count}×)` : ""}`
                  : "Link created — not emailed yet"}
                {fixRequest.completed_at ? ` · Completed ${new Date(fixRequest.completed_at).toLocaleString()}` : ""}
                {fixRequest.status === "open" && fixRequest.expires_at ? ` · Expires ${new Date(fixRequest.expires_at).toLocaleDateString()}` : ""}
              </p>
              {fixRequest.status === "open" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={() => {
                    const base = (import.meta.env.VITE_PUBLIC_APP_URL || "https://dashboard.creativeinvestments.africa").replace(/\/$/, "");
                    navigator.clipboard.writeText(`${base}/intake-fix/${fixRequest.token}`);
                    toast.success("Correction link copied");
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy correction link
                </Button>
              )}
            </div>
          )}

          {submission.passport_doc_verdict && (
            <div className={`rounded-md border p-3 ${flagged ? "border-destructive/50 bg-destructive/5" : "bg-muted/40"}`}>
              <div className="flex items-center gap-2 mb-1">
                {flagged ? <AlertTriangle className="h-4 w-4 text-destructive" /> : <ShieldCheck className="h-4 w-4" />}
                <p className="text-sm font-medium">
                  Automatic document check: {verdictLabel(submission.passport_doc_verdict)}
                  {confidencePct(submission.passport_doc_confidence) ? ` (${confidencePct(submission.passport_doc_confidence)} confidence)` : ""}
                </p>
              </div>
              {submission.passport_doc_reason && <p className="text-xs text-muted-foreground">{submission.passport_doc_reason}</p>}
              {submission.passport_doc_checked_at && (
                <p className="text-xs text-muted-foreground mt-1">Checked {new Date(submission.passport_doc_checked_at).toLocaleString()}</p>
              )}
              {submission.passport_doc_verified_by && (
                <Badge variant="secondary" className="mt-2">Manually confirmed by a team member</Badge>
              )}
              {flagged && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {onEmail && (
                    <Button size="sm" onClick={() => (onRequestFix ? onRequestFix(submission, ["passport_photo"]) : onEmail(submission))} disabled={!submission.email}>
                      <Mail className="h-4 w-4 mr-1" /> Ask for passport page
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => markVerified("passport_doc_verified_by")} disabled={overriding}>
                    {overriding && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} It is correct — confirm manually
                  </Button>
                </div>
              )}
            </div>
          )}

          {submission.id_doc_verdict && (
            <div className={`rounded-md border p-3 ${idFlagged ? "border-destructive/50 bg-destructive/5" : "bg-muted/40"}`}>
              <div className="flex items-center gap-2 mb-1">
                {idFlagged ? <AlertTriangle className="h-4 w-4 text-destructive" /> : <ShieldCheck className="h-4 w-4" />}
                <p className="text-sm font-medium">
                  ID photo check: {idPhotoVerdictLabel(submission.id_doc_verdict)}
                  {confidencePct(submission.id_doc_confidence) ? ` (${confidencePct(submission.id_doc_confidence)} confidence)` : ""}
                </p>
              </div>
              {submission.id_doc_reason && <p className="text-xs text-muted-foreground">{submission.id_doc_reason}</p>}
              {submission.id_doc_checked_at && (
                <p className="text-xs text-muted-foreground mt-1">Checked {new Date(submission.id_doc_checked_at).toLocaleString()}</p>
              )}
              {submission.id_doc_verified_by && (
                <Badge variant="secondary" className="mt-2">Manually confirmed by a team member</Badge>
              )}
              {idFlagged && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {onEmail && (
                    <Button size="sm" onClick={() => (onRequestFix ? onRequestFix(submission, ["id_photo"]) : onEmail(submission))} disabled={!submission.email}>
                      <Mail className="h-4 w-4 mr-1" /> Ask for a proper ID photo
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => markVerified("id_doc_verified_by")} disabled={overriding}>
                    {overriding && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} It is correct — confirm manually
                  </Button>
                </div>
              )}
            </div>
          )}



          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred name" value={submission.preferred_name} />
            <Field label="Date of birth" value={submission.date_of_birth} />
            <Field label="Course" value={submission.course} />
            <Field label="WhatsApp" value={submission.whatsapp_e164} />
            <Field label="Email" value={submission.email} />
            <Field label="Nationality" value={submission.nationality} />
            <Field label="Passport" value={submission.passport_number} />
            <Field label="Passport expiry" value={submission.passport_expiry} />
            <Field label="Submitted at" value={submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : null} />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Home address</p>
            <p className="text-sm">
              {[submission.home_address_line1, submission.home_address_line2, submission.home_city, submission.home_postal_code, submission.home_country].filter(Boolean).join(", ") || "—"}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Dietary tags" value={submission.dietary_tags} />
            <Field label="Dietary notes" value={submission.dietary_notes} />
            <Field label="Allergies" value={hasDeclaredValue(submission.allergies) ? submission.allergies : "None declared"} />
            {hasDeclaredValue(submission.allergies) && <Field label="Allergy severity" value={submission.allergy_severity} />}
            <Field label="Medical notes" value={hasDeclaredValue(submission.medical_notes) ? submission.medical_notes : "None declared"} />
          </div>

          <div className="border-t pt-3">
            <p className="font-medium text-sm mb-2">Next of kin</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" value={submission.nok_name} />
              <Field label="Relationship" value={submission.nok_relationship} />
              <Field label="WhatsApp" value={submission.nok_whatsapp_e164} />
              <Field label="Email" value={submission.nok_email} />
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="font-medium text-sm mb-2">Uploaded documents</p>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <div className="flex flex-wrap gap-2">
                {idUrl && <Button asChild variant="outline" size="sm"><a href={idUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> ID photo</a></Button>}
                {ppUrl && <Button asChild variant="outline" size="sm"><a href={ppUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> Passport</a></Button>}
                {!idUrl && !ppUrl && <p className="text-sm text-muted-foreground">No files attached.</p>}
              </div>
            )}
          </div>

          <div className="border-t pt-3">
            <p className="font-medium text-sm mb-1">Indemnity</p>
            <Badge variant={submission.indemnity_accepted ? "default" : "destructive"}>
              {submission.indemnity_accepted ? `Accepted (${submission.indemnity_version})` : "Not accepted"}
            </Badge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}